import {
  Box,
  Checkbox,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { AutoComplete, Button } from '@components';
import { addNewCustomer } from '@store/customers/actions';
import {
  fetchCustomerOfficeGroupsCall,
  Group,
  ICustomerDetails,
} from '@store/customers/customerDetails';
import { IUserSearch } from '@store/customers/types';
import { userStore } from '@store/user';
import { TrendingNeutralIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { isAdmin, isSuperAdmin } from '@utils/utils';
import {
  assignOfficeGroup,
  setAssignOfficeGroup,
} from '@views/customer/newCustomer/NewCustomer';
import { createSignal, For, Show } from 'solid-js';

import {
  createCustomerStore,
  setCreateCustomerStore,
  setupNewCustomerState,
} from '../NewCustomerTabPanel';
import classes from '../classes';
import CustomerHeader from './CustomerHeader';

type OwnerShipProps = {
  setActiveTab: (tab: number) => void;
  validateFields: () => void;
  file: () => File[];
};

type RenderItemsProps = {
  item2: IUserSearch;
};

const RenderItem = (props: RenderItemsProps) => (
  <Stack direction="column" class="overflow-auto max-h-[600px] flex! flex-col!">
    <Typography variant="body2" class="font-medium!">
      {props.item2.name}
    </Typography>

    <Typography variant="body2">
      Office: {props.item2.office} - {props.item2.accountingGroup}
    </Typography>
    <Typography variant="body2">Email: {props.item2.userName}</Typography>
    <Typography variant="body2">Phone: {props.item2.phone}</Typography>
  </Stack>
);

const OwnerShipTab = (props: OwnerShipProps) => {
  const [localAccountOfficeId, setLocalAccountOfficeId] = createSignal<
    string | number
  >(createCustomerStore.customer.accountManagerOfficeId ?? '');
  const [loadingOffice, setLoadingOffice] = createSignal(false);

  const fetchAssignGroupData = async (officeId: number) => {
    try {
      setLoadingOffice(true);
      const resp = await fetchCustomerOfficeGroupsCall(officeId);
      if (resp && resp.isSuccess) {
        if (
          officeId == localAccountOfficeId() ||
          officeId == createCustomerStore.customer.salesManagerOfficeId
        )
          resp.value = resp.value.filter(
            (x) => x.name != 'Managers' && x.name != 'Admin',
          );
        const assignOfficeGroupRes = resp.value.map((item) => {
          return {
            ...item,
            label: `${item.name as string} (${item.office})`,
            value: item.id,
          };
        });
        setAssignOfficeGroup(assignOfficeGroupRes);
      }
    } finally {
      setLoadingOffice(false);
    }
  };

  const handleAssignGroupSelect = async (group: Group) => {
    await fetchAssignGroupData(group.id);
  };

  const handleAssignGroupChange = (values: Partial<ICustomerDetails>) => {
    setupNewCustomerState({
      ...createCustomerStore.customer,
      ...values,
    });
  };

  const editable = () => {
    const roles = userStore.user.roles || [];
    return (
      roles.includes('Admin') ||
      roles.includes('Manager') ||
      roles.includes('Recruiter') ||
      roles.includes('Supervisor')
    );
  };

  const handleGroupCheckboxChange = (group: Group, setFalse: boolean) => {
    if (!editable()) return;
    const groups = [...(createCustomerStore.customer.groups ?? [])];
    const index = groups.findIndex((g) => g.id === group.id);

    if (setFalse && index === -1) {
      groups.push({ ...group });
    } else if (!setFalse && index >= 0) {
      groups.splice(index, 1);
    }
    handleAssignGroupChange({ groups });
  };

  const handleGrouplistSelect = (item: IUserSearch, name: string) => {
    setupNewCustomerState({
      [`${name}ManagerName`]: item.name,
      [`${name}ManagerId`]: item.id,
    });
  };

  const RenderOfficeOptions = (props: { groups: Group[] | undefined }) => {
    const localGroups = () => (props.groups ?? []).map((x) => x.id);
    return (
      <Box
        class={`bg-[white] rounded ${
          assignOfficeGroup().length === 0 ||
          assignOfficeGroup().every((group) =>
            localGroups().includes(Number(group.value)),
          )
            ? 'border-none!'
            : 'border-[#026EA14D] border-2 p-2'
        }`}
      >
        <Show
          when={!loadingOffice()}
          fallback={<Skeleton variant="rectangular" width="100%" />}
        >
          <For each={assignOfficeGroup()}>
            {(group) => {
              return (
                <Box
                  class={`${
                    localGroups().includes(Number(group.value)) ? 'hidden' : ''
                  }`}
                >
                  <Checkbox
                    size="xs"
                    color="info"
                    label={group.label}
                    checked={false}
                    onChange={() => {
                      handleGroupCheckboxChange(group, true);
                    }}
                    dataTestId={`new-customer-ownership-assign-group-${group.label}-checkboxInput`}
                  />
                </Box>
              );
            }}
          </For>
        </Show>
      </Box>
    );
  };

  const submitForm = async () => {
    const cb = (id: string) => {
      setCreateCustomerStore('responseData', {
        id,
        successMessage: 'Customer Saved Successfully',
      });
    };
    try {
      setCreateCustomerStore('isLoading', true);
      props.setActiveTab(4); //Sets the active tab to the fourth tab.
      props.validateFields();
      await addNewCustomer({
        customer: {
          ...createCustomerStore.customer,
          paymentTerms:
            Number(createCustomerStore.customer.paymentType) === 0
              ? 'Net 30'
              : 'Prepay',
          //Added this to set the id to 0 for submit for credit prospect flow
          contacts: createCustomerStore.customer.contacts?.map((contact) => {
            return {
              ...contact,
              id: 0,
            };
          }),
        },
        file: props.file(),
        cb,
      });
    } finally {
      setCreateCustomerStore('isLoading', false);
    }
  };

  return (
    <Box class={classes.container}>
      <CustomerHeader
        title="Ownership"
        subTitle="Confirm who will own and have access to this customer."
      />
      <Grid container spacing={12}>
        <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 3 }}>
          <Box class="mb-[15px]">
            <AutoComplete
              endpoint="user/autocomplete/?term="
              version="v3"
              responseFieldName="name"
              onItemSelect={async (item) => {
                const { officeId = '' } = item as { officeId: number };
                setLocalAccountOfficeId(officeId);
                handleGrouplistSelect(item as IUserSearch, 'account');
                await fetchAssignGroupData(officeId as number);
              }}
              id="autocomplete-accountManagerName"
              label="Account Manager"
              value={createCustomerStore.customer.accountManagerName ?? ''}
              renderItem={(item) => {
                const item2 = item as IUserSearch;
                return <RenderItem item2={item2} />;
              }}
              onClearValue={() => {
                setupNewCustomerState({
                  accountManagerName: '',
                  accountManagerId: null,
                });
              }}
            />
          </Box>
          <AutoComplete
            endpoint="user/autocomplete/?term="
            version="v3"
            responseFieldName="name"
            onItemSelect={(item) =>
              handleGrouplistSelect(item as IUserSearch, 'sales')
            }
            id="autocomplete-field-user"
            label="Sales Manager"
            value={createCustomerStore.customer.salesManagerName ?? ''}
            renderItem={(item) => {
              const item2 = item as IUserSearch;
              return <RenderItem item2={item2} />;
            }}
            onClearValue={() => {
              setupNewCustomerState({
                salesManagerName: '',
                salesManagerId: null,
              });
            }}
          />
          <Checkbox
            color="info"
            class="mt-[15px]! ml-[-9px] w-full max-w-full [&>span]:whitespace-normal! [&>span]:break-words!"
            size="xs"
            label="When checked, this customer (and all associated loads) will be visible to everyone in the same office as the selected Account Manager."
            checked={createCustomerStore.customer.isPublic}
            onChange={(value) => {
              setupNewCustomerState({ isPublic: value });
            }}
            dataTestId="new-customer-ownership-is-public-checkboxInput"
          />
        </Grid>

        <Grid
          item
          size={{ xs: 12, sm: 12, md: 12, lg: 4.2 }}
          class="border bg-[#f5f5f5] rounded p-[15px]!"
        >
          {isAdmin() || isSuperAdmin() ? (
            <AutoComplete
              class="bg-white!"
              endpoint="office/autocomplete/?term="
              version="v3"
              responseFieldName="name"
              onItemSelect={(item) => handleAssignGroupSelect(item as Group)}
              id="select-office-manage-group-section"
              label="Assign Group"
              value=""
              placeholder="Select Office"
            />
          ) : (
            <Box class="text-[12px] font-bold">Assign Groups</Box>
          )}
          <Grid class="overflow-auto max-h-[500px] flex! flex-col!">
            <Box class="flex flex-wrap p-2">
              <For each={createCustomerStore.customer.groups}>
                {(group) => (
                  <Box class="mr-2">
                    <Checkbox
                      size="xs"
                      color="info"
                      class="ml-[-9px]"
                      label={`${
                        Boolean(group.office)
                          ? `${group.name} (${group.office})`
                          : `${group.name}`
                      }`}
                      checked={true}
                      onChange={() => {
                        handleGroupCheckboxChange(group, false);
                      }}
                      dataTestId={`new-customer-ownership-customer-group-checkboxInput-${group.id}`}
                    />
                  </Box>
                )}
              </For>
            </Box>
            <RenderOfficeOptions groups={createCustomerStore.customer.groups} />
          </Grid>
          <Show when={assignOfficeGroup().length === 0}>
            <Box class="bg-[#f5f5f5] p-2 my-2 text-center border border-[#e3e3e3] rounded-sm">
              -- You have no groups --
            </Box>
          </Show>
        </Grid>
      </Grid>
      <Divider class="mt-[20px]!" />
      <Box class="flex w-full justify-center mt-[5px] mb-[20px] gap-6">
        <Button
          size="lg"
          startIcon={<TrendingNeutralIcon class="rotate-180" />}
          variant="contained"
          label="Previous"
          onClick={() => props.setActiveTab(2)}
          disabled={loadingOffice()}
          dataTestId={formatDataTestId('Previous', 'button')}
        />
        <Button
          size="lg"
          endIcon={<TrendingNeutralIcon />}
          variant="contained"
          label="Next"
          onClick={submitForm}
          disabled={loadingOffice()}
          dataTestId={formatDataTestId('Next', 'button')}
        />
      </Box>
    </Box>
  );
};

export default OwnerShipTab;
